/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Dropdown actions and calendar navigation handlers for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsNavigationHandlers
 */

import { useCallback } from "react";
import { getAppointmentStatusClassKey } from "../../utils/appointments/appointmentStatusDisplay.js";
import useViewAppointmentsAppointmentHandlers from "./useViewAppointmentsAppointmentHandlers.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers
 * @param {Object} virtualHandlers - Virtual appointment handlers
 * @param {Object} filterHandlers - Filter handlers (toggleDropdown)
 * @returns {Object} Navigation and dropdown action handlers
 */
const useViewAppointmentsNavigationHandlers = (
  ctx,
  fetching,
  virtualHandlers,
  filterHandlers,
) => {
  const { navigate, filteredRequests, activeDropdownId } = ctx;
  const { canMakeVirtual, handleMakeVirtual } = virtualHandlers;
  const { toggleDropdown } = filterHandlers;

  const appointmentHandlers = useViewAppointmentsAppointmentHandlers(
    ctx,
    fetching,
  );
  const { handleSeeProfile, handleSeeRequest, handleAppointmentReschedule } =
    appointmentHandlers;

  const handleDateSelect = useCallback((_date, _hour) => {
    // Calendar slot selection reserved for a future appointment-creation flow.
  }, []);

  const getAppointmentStatusStyleKey = useCallback(
    (appointment) => getAppointmentStatusClassKey(appointment) || "statusDefault",
    [],
  );

  const getActiveDropdownRequest = useCallback(
    () => filteredRequests.find((request) => request._id === activeDropdownId),
    [filteredRequests, activeDropdownId],
  );

  const handleActiveDropdownSeeProfile = useCallback(() => {
    const request = getActiveDropdownRequest();
    if (request) {
      handleSeeProfile(request);
    }
  }, [getActiveDropdownRequest, handleSeeProfile]);

  const handleActiveDropdownSeeRequest = useCallback(() => {
    const request = getActiveDropdownRequest();
    if (request) {
      handleSeeRequest(request);
    }
  }, [getActiveDropdownRequest, handleSeeRequest]);

  const handleActiveDropdownMakeVirtual = useCallback(() => {
    const request = getActiveDropdownRequest();
    if (request) {
      handleMakeVirtual(request);
    }
  }, [getActiveDropdownRequest, handleMakeVirtual]);

  const isActiveDropdownMakeVirtualDisabled = useCallback(() => {
    const request = getActiveDropdownRequest();
    if (!request) {
      return false;
    }
    const { estadoCitaVirtual } = request;
    const hasVirtualStatus =
      estadoCitaVirtual === "pending" || estadoCitaVirtual === "approved";
    return hasVirtualStatus || !canMakeVirtual(request);
  }, [getActiveDropdownRequest, canMakeVirtual]);

  const handleActionsDropdownClick = useCallback(
    (event) => {
      const requestId = event.currentTarget.dataset.requestId;
      if (requestId) {
        toggleDropdown(requestId, event);
      }
    },
    [toggleDropdown],
  );

  const handleNavigateBack = useCallback(() => {
    navigate("/businessEvents");
  }, [navigate]);

  return {
    handleDateSelect,
    getAppointmentStatusStyleKey,
    handleSeeProfile,
    handleSeeRequest,
    handleAppointmentReschedule,
    handleActiveDropdownSeeProfile,
    handleActiveDropdownSeeRequest,
    handleActiveDropdownMakeVirtual,
    isActiveDropdownMakeVirtualDisabled,
    handleActionsDropdownClick,
    handleNavigateBack,
  };
};

export default useViewAppointmentsNavigationHandlers;
